import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const CATEGORY_LABELS = {
  "iverali": "Lesni iverali in plošče",
  "delovne-plosce": "Delovne plošče",
  "kuhinjski-elementi": "Kuhinjski elementi",
  "pohistveno-okovje": "Pohištveno okovje",
};

const DECORS = ["Hrast", "Oreh", "Jesen", "Bela sijaj", "Skrilavec", "Beton", "Surov"];
const THICKNESSES = [8, 18, 25, 38];
const SORTS = [
  { id: "", label: "Privzeto" },
  { id: "price_asc", label: "Cena: najprej nižja" },
  { id: "price_desc", label: "Cena: najprej višja" },
  { id: "name", label: "Po imenu" },
];

export default function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = searchParams.get("category") || "";
  const q = searchParams.get("q") || "";
  const decor = searchParams.get("decor") || "";
  const thickness = searchParams.get("thickness") || "";
  const sort = searchParams.get("sort") || "";
  const akcija = searchParams.get("akcija") || "";

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category) params.category = category;
    if (q) params.q = q;
    if (decor) params.decor = decor;
    if (thickness) params.thickness = thickness;
    if (sort) params.sort = sort;
    if (akcija) params.akcija = true;
    api.get("/products", { params })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category, q, decor, thickness, sort, akcija]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const title = useMemo(() => {
    if (q) return `Rezultati iskanja: „${q}"`;
    if (akcija) return "Akcijska ponudba";
    if (category) return CATEGORY_LABELS[category] || "Trgovina";
    return "Vsa ponudba";
  }, [q, category, akcija]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-testid="catalog-page">
      <p className="font-tech text-xs uppercase tracking-[0.2em] text-[#B45309] mb-2">Trgovina</p>
      <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight text-[#1A1D1A] mb-8" data-testid="catalog-title">
        {title}
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <aside className="lg:col-span-3 bg-white rounded-2xl border border-[#E4E0D7] p-5 space-y-6 lg:sticky lg:top-32" data-testid="catalog-filters">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#1A1D1A]">
            <SlidersHorizontal className="w-4 h-4 text-[#B45309]" /> Filtri
          </div>

          <div>
            <p className="text-xs font-tech uppercase tracking-wider text-stone-500 mb-2">Iskanje</p>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                data-testid="catalog-search-input"
                defaultValue={q}
                onKeyDown={(e) => { if (e.key === "Enter") setParam("q", e.target.value.trim()); }}
                placeholder="Ime izdelka …"
                className="w-full h-10 pl-9 pr-3 rounded-lg bg-[#FBF9F5] border border-[#E4E0D7] text-sm focus:outline-none focus:ring-2 focus:ring-[#2D4030]/30"
              />
            </div>
          </div>

          <div>
            <p className="text-xs font-tech uppercase tracking-wider text-stone-500 mb-2">Kategorija</p>
            <div className="flex flex-wrap gap-1.5">
              <FilterChip testid="filter-category-all" active={!category} onClick={() => setParam("category", "")}>Vse</FilterChip>
              {Object.entries(CATEGORY_LABELS).map(([id, label]) => (
                <FilterChip key={id} testid={`filter-category-${id}`} active={category === id} onClick={() => setParam("category", id)}>
                  {label}
                </FilterChip>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-tech uppercase tracking-wider text-stone-500 mb-2">Dekor</p>
            <div className="flex flex-wrap gap-1.5">
              <FilterChip testid="filter-decor-all" active={!decor} onClick={() => setParam("decor", "")}>Vsi</FilterChip>
              {DECORS.map((d) => (
                <FilterChip key={d} testid={`filter-decor-${d.toLowerCase().replace(/ /g, "-")}`} active={decor === d} onClick={() => setParam("decor", d)}>
                  {d}
                </FilterChip>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-tech uppercase tracking-wider text-stone-500 mb-2">Debelina</p>
            <div className="flex flex-wrap gap-1.5">
              <FilterChip testid="filter-thickness-all" active={!thickness} onClick={() => setParam("thickness", "")}>Vse</FilterChip>
              {THICKNESSES.map((t) => (
                <FilterChip key={t} testid={`filter-thickness-${t}`} active={thickness === String(t)} onClick={() => setParam("thickness", String(t))}>
                  {t} mm
                </FilterChip>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-tech uppercase tracking-wider text-stone-500 mb-2">Razvrsti</p>
            <select
              data-testid="catalog-sort-select"
              value={sort}
              onChange={(e) => setParam("sort", e.target.value)}
              className="w-full h-10 px-3 rounded-lg bg-[#FBF9F5] border border-[#E4E0D7] text-sm focus:outline-none"
            >
              {SORTS.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>
        </aside>

        <div className="lg:col-span-9">
          {loading ? (
            <p className="text-stone-500 py-16 text-center" data-testid="catalog-loading">Nalagam izdelke …</p>
          ) : products.length === 0 ? (
            <div className="text-center py-16" data-testid="catalog-empty">
              <p className="text-stone-600 font-semibold">Ni najdenih izdelkov.</p>
              <p className="text-sm text-stone-500 mt-1">Poskusite z drugimi filtri ali iskalnim nizom.</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-stone-500 mb-5 font-tech" data-testid="catalog-count">{products.length} izdelkov</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {products.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${
        active
          ? "bg-[#2D4030] border-[#2D4030] text-[#FBF9F5]"
          : "bg-[#FBF9F5] border-[#E4E0D7] text-stone-600 hover:border-[#2D4030]/50"
      }`}
    >
      {children}
    </button>
  );
}
